import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useAction } from '../../context';
import { Failure, Field } from '../../bits';
import { Panel } from '../../kit';
import { useT } from '../../../messages';
import type { TenantCatalog } from '../../../wire';
import { headingMap } from './text';

/**
 * The three things done to the catalog as a whole rather than to one item —
 * rename a heading, add one thing, seed from a file. Each opens in the right
 * half in place of the item editor, from the list's header ("Add", "More"), so
 * they are one click away and never in the way of the list.
 */

/* ------------------------------------------------------------------ */
/* Headings                                                            */
/* ------------------------------------------------------------------ */

/**
 * Rename a heading everywhere it appears. A heading is a column, not a row
 * (0024 decision 1), so this is one verb over every item filed under it —
 * presentation, allowed whatever the business's stage.
 */
export function RenameHeading({
  tenantId,
  catalog,
  onChanged,
  onClose,
}: {
  tenantId: string;
  catalog: TenantCatalog;
  onChanged: () => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [result, setResult] = useState<string | null>(null);

  // Every heading any item carries, in any language — renaming is a match on
  // the stored value, so the operator chooses from what is actually there.
  const headings = Array.from(
    new Set(catalog.items.flatMap((i) => Object.values(headingMap(i) ?? {}))),
  ).sort();

  const rename = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.renameCatalogCategory(tenantId, { from, to: to.trim() || null });
      setResult(t('catalog.renamed', { items: answer.items, to: answer.to ?? '—' }));
    });
    if (ok) {
      setFrom('');
      setTo('');
      onChanged();
    }
  };

  return (
    <Panel title={t('catalog.categories')} actions={<CloseButton onClose={onClose} />}>
      <p className="muted">{t('catalog.categoriesNote')}</p>
      {headings.length === 0 ? (
        <p className="muted">{t('catalog.tools.noHeadings')}</p>
      ) : (
        <>
          <div className="catalog-fields">
            <Field label={t('catalog.renameFrom')}>
              <select
                aria-label={t('catalog.renameFrom')}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              >
                <option value="">{t('catalog.pick')}</option>
                {headings.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('catalog.renameTo')} hint={t('catalog.renameToHint')}>
              <input
                aria-label={t('catalog.renameTo')}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Field>
          </div>
          <div className="row-actions">
            <button
              type="button"
              className="button button--primary button--sm"
              disabled={from === '' || action.busy}
              onClick={() => void rename()}
            >
              {t('catalog.rename')}
            </button>
          </div>
        </>
      )}
      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      {result !== null ? <p className="ok">{result}</p> : null}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* One new thing                                                       */
/* ------------------------------------------------------------------ */

/**
 * A service or a shelf product. A dish, a room type and a departure each need a
 * document rather than a form, and the seeder takes that document. Offered only
 * while the business is being set up — after that a new item is the owner's.
 */
export function AddItem({
  tenantId,
  onCreated,
  onClose,
}: {
  tenantId: string;
  onCreated: (itemId: string) => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [kind, setKind] = useState<'scheduled' | 'stocked'>('scheduled');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('60');

  const cents = Math.round(Number(price) * 100);
  const valid =
    name.trim() !== '' &&
    price.trim() !== '' &&
    Number.isFinite(cents) &&
    cents >= 0 &&
    (kind === 'stocked' || Number(duration) > 0);

  const create = async (): Promise<void> => {
    let created: string | undefined;
    const ok = await action.run(async () => {
      const answer = await api.createCatalogItem(tenantId, {
        kind,
        name: name.trim(),
        price_cents: cents,
        ...(kind === 'scheduled' ? { duration_min: Number(duration) } : {}),
      });
      created = answer.item_id;
    });
    if (ok && created !== undefined) onCreated(created);
  };

  return (
    <Panel title={t('catalog.add')} actions={<CloseButton onClose={onClose} />}>
      <p className="muted">{t('catalog.addNote')}</p>
      <div className="catalog-fields">
        <Field label={t('catalog.kind')}>
          <select
            aria-label={t('catalog.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value === 'stocked' ? 'stocked' : 'scheduled')}
          >
            <option value="scheduled">{t('catalog.tab.scheduled')}</option>
            <option value="stocked">{t('catalog.tab.stocked')}</option>
          </select>
        </Field>
        <Field label={t('catalog.name')}>
          <input
            aria-label={t('catalog.newName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label={t('catalog.price')}>
          <input
            inputMode="decimal"
            aria-label={t('catalog.newPrice')}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </Field>
        {kind === 'scheduled' ? (
          <Field label={t('catalog.duration')}>
            <input
              type="number"
              aria-label={t('catalog.newDuration')}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </Field>
        ) : null}
      </div>
      <div className="row-actions">
        <button
          type="button"
          className="button button--primary button--sm"
          disabled={!valid || action.busy}
          onClick={() => void create()}
        >
          {t('catalog.create')}
        </button>
      </div>
      {action.failure !== null ? <Failure failure={action.failure} /> : null}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* The catalog file                                                    */
/* ------------------------------------------------------------------ */

/**
 * Paste a catalog file, read what it would do, then do it.
 *
 * The Website tab's **Seed this** hands the owner's own menu straight into this
 * box: the owner's menu becomes a catalog file, an operator reads the dry run,
 * and the business has a catalog — with no terminal anywhere in it.
 *
 * Dry run first, always, and **Seed this** stays disabled until a dry run of
 * *this* text has come back. A seed rewrites a whole catalog, and an operator
 * who meant to look should not be one click from having done it.
 */
export function SeedFromFile({
  tenantId,
  commerceEditable,
  initial,
  onChanged,
  onClose,
}: {
  tenantId: string;
  commerceEditable: boolean;
  initial?: string | undefined;
  onChanged: () => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [file, setFile] = useState(initial ?? '');
  const [report, setReport] = useState<string | null>(null);
  const [checked, setChecked] = useState<string | null>(null);

  const run = async (apply: boolean): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.seedCatalog(tenantId, file, apply);
      setReport(
        t(apply ? 'catalog.seedApplied' : 'catalog.seedDryRun', {
          services: answer.services,
          resources: answer.resources,
          stays: answer.stays,
          departures: answer.departures,
          version: answer.catalog_version,
        }),
      );
      setChecked(apply ? null : file);
    });
    if (ok && apply) onChanged();
  };

  return (
    <Panel title={t('catalog.seed')} actions={<CloseButton onClose={onClose} />}>
      {!commerceEditable ? (
        <p className="muted">{t('catalog.tools.seedLive')}</p>
      ) : (
        <>
          <p className="muted">{t('catalog.seedNote')}</p>
          <Field label={t('catalog.seedFile')} hint={t('catalog.tools.seedFileHint')}>
            <textarea
              rows={10}
              className="catalog-seed"
              aria-label={t('catalog.seedFile')}
              value={file}
              onChange={(e) => {
                setFile(e.target.value);
                setChecked(null);
                setReport(null);
              }}
            />
          </Field>
          <div className="row-actions">
            <button
              type="button"
              className="button button--sm"
              disabled={file.trim() === '' || action.busy}
              onClick={() => void run(false)}
            >
              {t('catalog.dryRun')}
            </button>
            <button
              type="button"
              className="button button--primary button--sm"
              disabled={checked !== file || action.busy}
              onClick={() => void run(true)}
            >
              {t('catalog.seedThis')}
            </button>
          </div>
          {action.failure !== null ? <Failure failure={action.failure} /> : null}
          {report !== null ? <p className="ok">{report}</p> : null}
        </>
      )}
    </Panel>
  );
}

function CloseButton({ onClose }: { onClose: () => void }): ReactNode {
  const t = useT();
  return (
    <button type="button" className="button button--quiet button--sm" onClick={onClose}>
      {t('catalog.tools.close')}
    </button>
  );
}
